import { useTranslation } from 'react-i18next';
import { useAppUrl } from '../lib/appUrl';

function CheckPill({ children }) {
  return (
    <div className="flex items-center gap-2 text-sm text-text-muted">
      <svg className="h-4 w-4 shrink-0 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
        <polyline points="20 6 9 17 4 12" />
      </svg>
      <span>{children}</span>
    </div>
  );
}

function ChatMockup() {
  const { t } = useTranslation();
  return (
    <div className="animate-float-in mx-auto w-full max-w-md rounded-2xl border border-border bg-surface glow-primary">
      <div className="flex items-center gap-3 border-b border-border px-4 py-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-primary to-primary-2 text-sm font-bold text-primary-fg">
          AI
        </div>
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-text">{t('hero.mockup.header')}</p>
          <p className="flex items-center gap-1.5 text-xs text-success">
            <span className="inline-block h-2 w-2 rounded-full bg-success" />
            {t('hero.mockup.status')}
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-3 p-4">
        <div className="max-w-[80%] self-start rounded-2xl rounded-ss-sm bg-surface-elevated px-4 py-2.5 text-sm text-text">
          {t('hero.mockup.q1')}
        </div>
        <div className="max-w-[85%] self-end rounded-2xl rounded-se-sm bg-primary px-4 py-2.5 text-sm text-primary-fg">
          {t('hero.mockup.a1')}
        </div>
        <div className="max-w-[80%] self-start rounded-2xl rounded-ss-sm bg-surface-elevated px-4 py-2.5 text-sm text-text">
          {t('hero.mockup.q2')}
        </div>
        <div className="max-w-[85%] self-end rounded-2xl rounded-se-sm bg-primary px-4 py-2.5 text-sm text-primary-fg">
          {t('hero.mockup.a2')}
        </div>
      </div>
    </div>
  );
}

export default function Hero() {
  const { t } = useTranslation();
  const appUrl = useAppUrl();
  return (
    <section className="relative overflow-hidden border-b border-border">
      <div aria-hidden className="pointer-events-none absolute inset-0 bg-grid opacity-[0.18]" />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-[420px] opacity-30"
        style={{
          background:
            'radial-gradient(60% 100% at 50% 0%, var(--color-primary) 0%, transparent 60%)',
        }}
      />

      <div className="relative mx-auto grid max-w-6xl items-center gap-12 px-4 py-16 sm:py-24 lg:grid-cols-2">
        <div className="text-center lg:text-start">
          <span className="inline-flex items-center gap-2 rounded-full border border-border bg-surface px-3 py-1 text-xs font-medium text-text-muted">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-primary" />
            {t('hero.badge')}
          </span>

          <h1 className="mt-6 text-balance text-4xl font-bold leading-[1.15] tracking-tight sm:text-5xl md:text-6xl">
            <span className="text-gradient">{t('hero.title')}</span>
          </h1>

          <p className="mx-auto mt-6 max-w-xl text-pretty text-base leading-relaxed text-text-muted sm:text-lg lg:mx-0">
            {t('hero.subtitle')}
          </p>

          <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row lg:justify-start">
            <a
              href={appUrl()}
              className="w-full rounded-lg bg-primary px-8 py-3 text-base font-semibold text-primary-fg shadow-lg shadow-primary/25 transition hover:bg-primary-hover sm:w-auto"
            >
              {t('hero.cta')}
            </a>
            <a
              href="#how"
              className="w-full rounded-lg border border-border bg-surface px-8 py-3 text-base font-medium text-text transition hover:border-border-strong sm:w-auto"
            >
              {t('hero.secondary')}
            </a>
          </div>

          <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:flex-wrap sm:gap-x-6 lg:justify-start">
            <CheckPill>{t('hero.trust.card')}</CheckPill>
            <CheckPill>{t('hero.trust.setup')}</CheckPill>
            <CheckPill>{t('hero.trust.isolated')}</CheckPill>
          </div>
        </div>

        <div className="relative">
          <div
            aria-hidden
            className="pointer-events-none absolute -inset-6 opacity-40 blur-2xl"
            style={{
              background:
                'radial-gradient(50% 50% at 50% 50%, var(--color-primary) 0%, transparent 70%)',
            }}
          />
          <div className="relative">
            <ChatMockup />
          </div>
        </div>
      </div>
    </section>
  );
}
